import ee from '../../services/earth-engine';
import { eeEval } from '../utils/index.js';
import * as Lyzenga from './lyzenga';
import { model1Apply, model2Apply, model3Apply, fitLinearCoefficientsSafe } from './modelResult.js';
import { SPM_BAND, spmTrainingFilter } from './spm';

/**
 * @function clearWaterSamples
 * @description The training soundings narrowed to pixels the empirical models can be fitted on:
 * every predictor present, and the water clear enough for the log ratio to mean anything - see
 * {@link spmTrainingFilter}.
 * @param {Object} [samples] - The sampled soundings, as an `ee.FeatureCollection`.
 * @param {Array<String>} fields - The predictor columns that must be present.
 * @memberof module:Bathymetry
 * @returns {Object|null} The filtered collection, or the falsy input unchanged.
 */
const clearWaterSamples = (samples, fields) =>
  samples &&
  ee
    .FeatureCollection(samples)
    .filter(ee.Filter.notNull([...fields, SPM_BAND, 'or_depth']))
    .filter(spmTrainingFilter());

/**
 * @function coefficientOf
 * @description One coefficient of a fit read back from Earth Engine, 0 when the fit has none - as
 * `ee.Dictionary.get(key, 0)` would give.
 * @param {Object} [values] - The fitted coefficients.
 * @param {String} key - The coefficient.
 * @memberof module:Bathymetry
 * @returns {Number|null}
 */
const coefficientOf = (values, key) => (values && key in values ? values[key] : 0);

/**
 * @function fitAnalyticModel
 * @description Fits one of the three published empirical models and applies it to the imagery.
 * In preset mode the coefficients come from the saved analysis rather than from a fit, so the
 * same imagery can be reprocessed without the soundings; they are read back either way, because
 * the metrics table reports them. Each model's coefficients are read in one request: every
 * request carries the samples, which for a large survey are megabytes.
 * @param {Object} params
 * @param {String} params.model - The model, as the wizard names it.
 * @param {Object} params.cleanImage - The imagery reduced to the three selected bands.
 * @param {Array<String>} params.RGB - Those bands, in order.
 * @param {Object} params.data - The analysis configuration.
 * @param {Object} [params.samples] - The sampled soundings, unused in preset mode.
 * **Called by:
 * @see {ProcessingPage} - Bathymetry's result page, on the run.
 * @memberof module:Bathymetry
 * @returns {Promise<Object>} `{image, coefficients}`.
 * @throws {Error} When the model is not one of the three.
 */
export const fitAnalyticModel = async ({ model, cleanImage, RGB, data, samples }) => {
  switch (model) {
    case 'Lyzenga': {
      const coef = data.presetMode ? ee.Dictionary(data.presetCoeficients[0]) : Lyzenga.fitLyzengaSafe(samples);
      const fitted = await eeEval(coef);
      const [c, a, b, d] = ['c', 'a', 'b', 'd'].map((key) => coefficientOf(fitted, key));
      const coefficients = { c, a, b, d };
      return { coefficients, image: model1Apply(cleanImage, coefficients, RGB, data.presetMode, data.bands) };
    }
    case 'Stumpf 2003': {
      const coef = data.presetMode
        ? ee.Dictionary(data.presetCoeficients[0])
        : fitLinearCoefficientsSafe(clearWaterSamples(samples, ['pSDB']), 'pSDB');
      const fitted = await eeEval(coef);
      const [m1, m0] = ['m1', 'm0'].map((key) => coefficientOf(fitted, key));
      const coefficients = { m1, m0 };
      return { coefficients, image: model2Apply(cleanImage, coefficients, RGB, data.presetMode, data.bands) };
    }
    case 'Caballero & Stumpf 2020': {
      const fitted = clearWaterSamples(samples, ['pSDBgreen', 'pSDBred']);
      const coefG = data.presetMode
        ? ee.Dictionary(data.presetCoeficients['colorGreen'])
        : fitLinearCoefficientsSafe(fitted, 'pSDBgreen');
      const coefR = data.presetMode
        ? ee.Dictionary(data.presetCoeficients['colorRed'])
        : fitLinearCoefficientsSafe(fitted, 'pSDBred');
      const image = model3Apply(cleanImage, coefG, coefR, RGB, data.presetMode, data.bands);
      const read = await eeEval(ee.Dictionary({ green: coefG, red: coefR }));
      const [greenM1, greenM0] = ['m1', 'm0'].map((key) => coefficientOf(read?.green, key));
      const [redM1, redM0] = ['m1', 'm0'].map((key) => coefficientOf(read?.red, key));
      return { image, coefficients: { green_m1: greenM1, green_m0: greenM0, red_m1: redM1, red_m0: redM0 } };
    }
    default:
      throw new Error(`Modelo inválido: ${model}`);
  }
};
